import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { selectTenantCounts } from '../slices/tenantCountsSlice';

import { selectTenantPoliciesMetadata } from '../slices/tenantPoliciesSlice';

const selectSits = (s: RootState) => s.sits.items; // UISit[]
const selectLabels = (s: RootState) => s.labels.data; // UILabel[]
const selectPolicies = (s: RootState) => s.policies.items; // UIPolicy[]

export const makeSelectTenantKPIBlocks = (tenantId: string) =>
  createSelector([selectTenantCounts, selectSits, selectLabels, selectPolicies, selectTenantPoliciesMetadata], (tenantCountsData, sits, labels, policies, tenantPoliciesMeta) => {
    const tenantCounts = tenantCountsData[tenantId];

    // With server-side pagination, tenantPolicies.length is only the current page (e.g. 25),
    // so it can't be used as the total. Prefer the server-reported total from the policies
    // listing response (DlpTotalCount + AutoLabelTotalCount = all policy types for this tenant).
    // Fall back to tenantCounts.totalPolicies if the listing hasn't loaded yet.
    const serverPoliciesTotal =
      (tenantPoliciesMeta.dlpTotalCount || 0) + (tenantPoliciesMeta.autoLabelTotalCount || 0);
    const actualPoliciesTotal = serverPoliciesTotal > 0 ? serverPoliciesTotal : undefined;
    // Deployed count can't be derived from a single page; defer to tenantCounts.deployedPolicies.
    const actualPoliciesDeployed: number | undefined = undefined;
    
    // If we have API data, use it; otherwise fall back to the old calculation
    if (tenantCounts) {
      return {
        sits: { 
          available: tenantCounts.deployedSITs, 
          total: tenantCounts.totalSITs 
        },
        labels: { 
          available: tenantCounts.deployedLabels, 
          total: tenantCounts.totalLabels 
        },
        policies: { 
          available: actualPoliciesDeployed ?? tenantCounts.deployedPolicies, 
          total: actualPoliciesTotal ?? tenantCounts.totalPolicies 
        },
        overall: tenantCounts.overallDeploymentPercentage,
      };
    }

    // Fallback to original calculation if API data is not available
    const countAvailable = (arr: { tenants?: { id: string; status?: string }[] }[]) =>
      arr.filter(
        (it) =>
          Array.isArray(it.tenants) &&
          it.tenants!.some(
            (t) =>
              t.id === tenantId &&
              typeof t.status === 'string' &&
              t.status.toLowerCase() === 'available',
          ),
      ).length;

    const totals = {
      sits: sits.length,
      labels: labels.length,
      policies: policies.length,
    };

    const available = {
      sits: countAvailable(sits),
      labels: countAvailable(labels),
      policies: countAvailable(policies),
    };

    const denom = Math.max(1, totals.sits + totals.labels + totals.policies);
    const overall = Math.round(
      ((available.sits + available.labels + available.policies) / denom) * 100,
    );

    return {
      sits: { available: available.sits, total: totals.sits },
      labels: { available: available.labels, total: totals.labels },
      policies: { available: available.policies, total: totals.policies },
      overall,
    };
  });
